/**
 * CR402 Phase 2: Live Card Preview with Highlight Overlay
 * CR406: Wrapped in preview-only ErrorBoundary - never break Studio
 * CR343: Force V2 renderer — never use DealGiftCard (V1) in Components preview
 * 
 * Renders the full deal card using V2 renderer with a bounding box highlight
 * around the selected component using data-component-key selectors.
 */

import React, { Component, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type ErrorInfo } from "react";
import { cn } from "@/lib/utils";
import type { Deal } from "@/lib/types/deals";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

// CR343: V2 renderer only
import { DealCardRendererV2 } from "@/components/deal-cards/DealCardRendererV2";
import { useDealCardSpecV2 } from "@/hooks/useDealCardSpecV2";
import { resolveSpecV2WithDraft, type V2DraftOverrides } from "@/lib/deal-cards/specV2/draftResolver";

// ================================================================
// TYPES
// ================================================================

type HighlightBox = { 
  top: number; 
  left: number; 
  width: number; 
  height: number;
} | null;

// ================================================================
// CR406: PREVIEW-ONLY ERROR BOUNDARY
// ================================================================

interface PreviewErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface PreviewErrorBoundaryProps {
  children: ReactNode;
  onReset?: () => void;
  label?: string;
}

class LivePreviewErrorBoundary extends Component<PreviewErrorBoundaryProps, PreviewErrorBoundaryState> {
  constructor(props: PreviewErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): PreviewErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[CR406] Live preview crashed:", {
      label: this.props.label || "LiveCardPreview",
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="border border-destructive/30 bg-destructive/5 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
            <div className="space-y-2 flex-1 min-w-0">
              <h4 className="font-medium text-sm text-destructive">Preview crashed</h4>
              <p className="text-xs text-muted-foreground">
                The live preview encountered an error. Your work is safe.
              </p>
              {this.state.error && (
                <code className="text-xs font-mono block text-destructive/80 truncate">
                  {this.state.error.message}
                </code>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={this.handleReset}
                className="mt-2 gap-1.5"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ================================================================
// TYPES (continued)
// ================================================================

interface LiveCardPreviewWithHighlightProps {
  selectedComponentKey: string | null;
  deal: Deal | null;
  dealType: string;
  /** Draft overrides to apply on top of the loaded V2 spec */
  draftOverrides?: V2DraftOverrides | null;
  /** Components config override for V2 spec resolution */
  componentsOverride?: Record<string, unknown> | null;
  hasUnsavedChanges?: boolean;
}

// ================================================================
// HELPERS
// ================================================================

function getBoxWithinContainer(
  container: HTMLElement, 
  el: HTMLElement
): HighlightBox {
  const c = container.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  return {
    top: r.top - c.top + container.scrollTop,
    left: r.left - c.left + container.scrollLeft,
    width: r.width,
    height: r.height,
  };
}

// ================================================================
// COMPONENT
// ================================================================

export function LiveCardPreviewWithHighlight({
  selectedComponentKey,
  deal,
  dealType,
  draftOverrides,
  componentsOverride,
  hasUnsavedChanges = false,
}: LiveCardPreviewWithHighlightProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [box, setBox] = useState<HighlightBox>(null);

  // CR343: Load V2 spec — always V2, no V1 fallback
  const { spec: baseSpec, loading: specLoading } = useDealCardSpecV2({ dealTypeKey: dealType });

  // CR344: Apply draft overrides + component config to spec for preview
  const previewSpec = useMemo(() => {
    if (!baseSpec) return null;
    
    if (!draftOverrides && !componentsOverride) return baseSpec;
    
    // Build a resolved spec with draft overrides applied
    const resolved = resolveSpecV2WithDraft(
      null,
      null,
      dealType,
      baseSpec.resolved.variantKey,
      baseSpec.resolved.sources,
      draftOverrides,
      null
    );
    
    // CR344: Merge componentsOverride into resolved.components so V2 block
    // renderers (RibbonBlock, etc.) see the draft component config (colors, labels, etc.)
    if (componentsOverride) {
      resolved.resolved.components = {
        ...resolved.resolved.components,
        ...componentsOverride,
      };
    }
    
    return resolved;
  }, [baseSpec, draftOverrides, componentsOverride, dealType]);

  // Recalculate bounding box
  const recalc = () => {
    const container = containerRef.current;
    if (!container || !selectedComponentKey) {
      setBox(null);
      return;
    }

    // Look for element with data-component-key
    const el = container.querySelector<HTMLElement>(
      `[data-component-key="${selectedComponentKey}"]`
    );

    if (!el) {
      setBox(null);
      return;
    }

    setBox(getBoxWithinContainer(container, el));
  };

  // Recalculate on selection or content change
  useLayoutEffect(() => {
    recalc();
  }, [selectedComponentKey, deal, draftOverrides]);

  // Listen for resize/scroll events
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const ro = new ResizeObserver(() => recalc());
    ro.observe(container);

    const onResize = () => recalc();
    const onScroll = () => recalc();

    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    container.addEventListener("scroll", onScroll);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
      container.removeEventListener("scroll", onScroll);
    };
  }, [selectedComponentKey]);

  if (!deal) {
    return (
      <div className="border rounded-lg p-8 bg-muted/20 text-center text-muted-foreground">
        Select a sample deal to see the preview
      </div>
    );
  }

  return (
    <LivePreviewErrorBoundary label="LiveCardPreviewWithHighlight">
      <div
        ref={containerRef}
        className="relative overflow-visible rounded-xl"
        style={{ minHeight: 400 }}
      >
        {/* CR343: V2 renderer only */}
        <div className="transform scale-[0.95] origin-top-left">
          {specLoading && (
            <Skeleton className="w-[340px] h-[440px] rounded-lg" />
          )}

          {!specLoading && !previewSpec && (
            <div className="w-[340px] h-[440px] rounded-lg border-2 border-dashed border-destructive/50 bg-destructive/5 flex flex-col items-center justify-center p-4 text-center">
              <AlertCircle className="h-8 w-8 text-destructive mb-2" />
              <p className="text-sm font-medium text-destructive">V2 Preview failed to load</p>
              <p className="text-xs text-muted-foreground mt-1">
                No V2 spec available for {dealType}.
              </p>
            </div>
          )}

          {!specLoading && previewSpec && (
            <DealCardRendererV2
              deal={deal}
              spec={previewSpec}
              isAdmin={true}
              surface="primary"
            />
          )}
        </div>

        {/* Highlight overlay */}
        {box && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute transition-all duration-150 ease-out"
            style={{
              top: box.top,
              left: box.left,
              width: box.width,
              height: box.height,
              border: "2px solid hsl(var(--primary))",
              borderRadius: 8,
              boxSizing: "border-box",
              boxShadow: "0 0 0 4px hsl(var(--primary) / 0.15)",
            }}
          />
        )}

        {/* Unsaved changes indicator */}
        {hasUnsavedChanges && (
          <div className="absolute top-2 right-2 px-2 py-1 bg-amber-100 text-amber-700 text-[10px] font-medium rounded-full">
            Preview (unsaved)
          </div>
        )}
      </div>
    </LivePreviewErrorBoundary>
  );
}

export default LiveCardPreviewWithHighlight;
